import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  NoEntryZone,
  NoEntryZoneSetting,
} from '@data/fms/noEntryZone/type';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type PostNoEntryZoneSettingBody = {
  name: string;
  description?: string;
  no_entry_zones: Omit<
    NoEntryZone,
    'no_entry_zone_id' | 'created_at' | 'updated_at'
  >[];
};

/**
 * 通行禁止区域設定作成 API
 */
export const useCreateNoEntryZoneSettingAPI = (): {
  state: APIState;
  postNoEntryZoneSetting: (
    body: PostNoEntryZoneSettingBody,
  ) => Promise<NoEntryZoneSetting | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      body: PostNoEntryZoneSettingBody,
    ): Promise<Result<NoEntryZoneSetting, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/no_entry_zone_settings`,
          body,
        );
        return new Success(res.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postNoEntryZoneSetting = useAtomCallback(
    useCallback(
      async (get, _, body: PostNoEntryZoneSettingBody) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.no_entry_zone_setting.create', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await request(project.id, environment.environment_id, body);
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.no_entry_zone_setting.create', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.no_entry_zone_setting.create', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postNoEntryZoneSetting,
  };
};

/**
 * 通行禁止区域設定適用 API
 */
export const useApplyNoEntryZoneSettingAPI = (): {
  state: APIState;
  postApplyNoEntryZoneSetting: (
    noEntryZoneSettingId: string,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      noEntryZoneSettingId: string,
      areaMapId: number,
      areaMapVersionId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/no_entry_zone_settings/${noEntryZoneSettingId}/area_maps/${areaMapId}/apply_target_area_map_versions/${areaMapVersionId}`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );
  const postApplyNoEntryZoneSetting = useAtomCallback(
    useCallback(
      async (get, _, noEntryZoneSettingId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.no_entry_zone_setting.apply.failed'),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          noEntryZoneSettingId,
          environment.area_map_id,
          environment.area_map_version_id,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t(
              'api.fms.no_entry_zone_setting.apply.failed',
            )}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.no_entry_zone_setting.apply.successfully'),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postApplyNoEntryZoneSetting,
  };
};

/**
 * 通行禁止区域設定解除 API
 */
export const useRevokeNoEntryZoneSettingAPI = (): {
  state: APIState;
  postRevokeNoEntryZoneSetting: (
    noEntryZoneSettingId: string,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      areaMapId: number,
      areaMapVersionId: string,
      noEntryZoneSettingId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/no_entry_zone_settings/${noEntryZoneSettingId}/area_maps/${areaMapId}/revoke_target_area_map_versions/${areaMapVersionId}`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );
  const postRevokeNoEntryZoneSetting = useAtomCallback(
    useCallback(
      async (get, _, noEntryZoneSettingId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.no_entry_zone_setting.revoke', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          environment.area_map_id,
          environment.area_map_version_id,
          noEntryZoneSettingId,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.no_entry_zone_setting.revoke', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.no_entry_zone_setting.revoke', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    postRevokeNoEntryZoneSetting,
  };
};
